<template>
  <div class="my-training-container">
    <v-skeleton-loader v-if="loading" height="100" type="image" class="rounded-xl custom-skeleton"></v-skeleton-loader>

    <div v-else class="calendar-card glass-card pa-3 rounded-xl">
      <v-progress-linear
        v-if="trainingProgress > 0"
        :value="trainingProgress"
        height="6"
        rounded
        color="primary"
        background-color="rgba(255,255,255,0.1)"
        class="mb-4"></v-progress-linear>

      <div class="days-row d-flex justify-space-between">
        <div
          v-for="(day, index) in calendar"
          :key="index"
          class="day-item"
          :class="{ 'day-active': hasActivity(day), 'day-today': day.today, 'day-selected': activeIndex === index }"
          @click="selectDay(day, index)">
          <span class="day-name caption">{{ day.short }}</span>
          <div class="day-circle">
            <v-icon v-if="hasActivity(day) && activeIndex !== index" x-small color="white">mdi-check-bold</v-icon>
            <span v-else class="day-date">{{ $moment(day.ts * 1000).format("D") }}</span>
          </div>
        </div>
      </div>

      <v-expand-transition>
        <div v-if="selectedDayData" class="day-detail mt-3 pa-3 rounded-lg glass-inner-card">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="font-weight-bold white--text text-uppercase d-flex align-center">
              <v-icon small color="white" class="mr-2">mdi-calendar-range</v-icon>
              {{ $moment(selectedDayData.date).format("dd, DD. MMMM") }}
            </span>
            <v-btn x-small icon @click="closeDetails">
              <v-icon small color="grey">mdi-close</v-icon>
            </v-btn>
          </div>

          <div v-if="loadingDayDetails" class="text-center pa-4">
            <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
          </div>

          <div v-else>
            <div
              v-if="!hasAnythingToDo(selectedDayData)"
              class="recharge-card relative d-flex flex-column align-center justify-center py-6 px-4 rounded-xl mt-2 overflow-hidden">
              <div class="ambient-glow"></div>

              <div class="icon-wrapper mb-3">
                <div class="pulse-ring"></div>
                <v-icon x-large color="#52ae32" class="z-index-2">mdi-flash</v-icon>
              </div>

              <h3 class="white--text text-h6 font-weight-black text-uppercase tracking-wider z-index-2 mb-1">Recovery Mode</h3>

              <p class="caption grey--text text--lighten-1 mb-3 z-index-2 text-center" style="max-width: 80%">
                Muskeln wachsen in der Ruhephase. <br />Entspann dich, Champ.
              </p>

              <div class="status-pill d-flex align-center px-3 py-1 rounded-pill z-index-2">
                <v-icon x-small color="#52ae32" class="mr-2 blink">mdi-heart-pulse</v-icon>
                <span class="caption font-weight-bold" style="color: #52ae32; letter-spacing: 1px">REGENERATION AKTIV</span>
              </div>
            </div>

            <div v-if="selectedDayData.checkin" class="activity-row d-flex align-center mb-2 pa-3 rounded-lg success-gradient-soft">
              <v-avatar size="32" color="rgba(0,0,0,0.2)" class="mr-3">
                <v-icon small color="white">mdi-map-marker-check</v-icon>
              </v-avatar>
              <div class="d-flex flex-column">
                <span class="caption font-weight-bold white--text">Studio Check-in</span>
                <span class="caption white--text opacity-80">
                  {{ $moment(selectedDayData.checkin).format("HH:mm") }}
                  <span v-if="selectedDayData.checkout">- {{ $moment(selectedDayData.checkout).format("HH:mm") }}</span>
                </span>
              </div>
            </div>

            <div
              v-for="(activity, i) in combinedActivities"
              :key="i"
              class="activity-row d-flex align-center mb-2 pa-3 rounded-lg"
              :style="activityStyle(activity)">
              <v-avatar size="32" :color="activity.type === 'Kurs' ? 'primary' : 'rgba(255,255,255,0.1)'" class="mr-3">
                <v-icon x-small color="white">{{ activity.type === "Kurs" ? "mdi-account-group" : "mdi-dumbbell" }}</v-icon>
              </v-avatar>
              <div class="d-flex flex-column">
                <span class="caption font-weight-bold white--text">{{ activity.title }}</span>
                <span class="caption grey--text">{{ activity.type }}</span>
              </div>
            </div>

            <div v-if="userService.hasRight('gf_cp_instructor')" class="mt-3 pt-2">
              <v-btn block small color="primary" class="font-weight-bold" @click="showGFHours = !showGFHours">
                <v-icon left x-small>mdi-invoice-clock-outline</v-icon> {{ showGFHours ? "Schließen" : "Abrechnung" }}
              </v-btn>
              <v-expand-transition>
                <div v-if="showGFHours" class="mt-2 glass-inner-card pa-2 rounded">
                  <div v-if="fetchingGFHours" class="text-center">
                    <v-progress-circular size="20" indeterminate color="primary"></v-progress-circular>
                  </div>
                  <div v-else>
                    <div v-if="!GFHourInfos || !GFHourInfos.list.length" class="caption grey--text text-center">Keine Stunden gefunden.</div>
                    <div v-else>
                      <div class="caption white--text mb-1 font-weight-bold">Gesamt: {{ GFHourInfos.totalDuration }} Min</div>
                      <div
                        v-for="(c, idx) in GFHourInfos.list"
                        :key="'gf' + idx"
                        class="caption grey--text d-flex justify-space-between border-bottom-light py-1">
                        <span>{{ c.courseName }}</span
                        ><span>{{ c.duration }}m</span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </div>
      </v-expand-transition>
    </div>
  </div>
</template>

<script>
import api from "../../api"
import userService from "../../services/userService"

export default {
  name: "myTraining",
  data() {
    return {
      userService,
      loading: true,
      calendar: [],
      trainingProgress: 0,

      // Selection State
      activeIndex: null,
      selectedDayData: null,
      loadingDayDetails: false,

      // Instructor State
      showGFHours: false,
      fetchingGFHours: false,
      GFHourInfos: null,
    }
  },
  computed: {
    combinedActivities() {
      if (!this.selectedDayData) return []
      const courses = (this.selectedDayData.courseArray || []).map((c) => ({ type: "Kurs", title: c.course.name }))
      const workouts = (this.selectedDayData.workouts || []).map((w) => ({ type: "Training", title: w.title }))
      return [...courses, ...workouts]
    },
  },
  async created() {
    await this.createCalendar()
    this.loading = false
  },
  methods: {
    async createCalendar() {
      try {
        let calendarData = await api.fetchTrainingCalendar()
        this.calendar = calendarData.calendar

        let aCount = 0
        this.calendar.forEach((e) => {
          if (this.hasActivity(e)) aCount++
        })
        this.trainingProgress = Math.round((100 / 7) * aCount)
      } catch (e) {
        console.error("Calendar fetch error", e)
      }
    },

    hasActivity(day) {
      return day && (day.foundWorkouts?.length || day.courseArray?.length || day.courses?.length || day.checkins?.length)
    },

    hasAnythingToDo(day) {
      return day && (day.checkin || day.workouts?.length || day.courseArray?.length)
    },

    // Style Helper für die Aktivitäten-Liste
    activityStyle(activity) {
      return {
        background: "rgba(255, 255, 255, 0.05)",
        borderLeft: activity.type === "Kurs" ? "3px solid #52ae32" : "3px solid #52ae32",
      }
    },

    closeDetails() {
      this.selectedDayData = null
      this.activeIndex = null
      this.showGFHours = false
    },

    async selectDay(day, index) {
      // Wenn der gleiche Tag nochmal geklickt wird -> schließen
      if (this.activeIndex === index) {
        this.closeDetails()
        return
      }

      this.activeIndex = index
      this.loadingDayDetails = true
      // Platzhalter, damit das Fenster sofort aufgeht
      this.selectedDayData = { date: day.ts * 1000 }
      this.showGFHours = false

      try {
        const dateString = this.$moment(day.ts * 1000).format("YYYY-MM-DD")

        // Instructor Daten holen, falls nötig
        if (this.userService.hasRight("gf_cp_instructor")) {
          this.fetchGFHours(dateString)
        }

        const dayData = await api.fetchTrainingDataForDay(dateString)
        this.selectedDayData = { ...dayData, date: day.ts * 1000 }
      } catch (e) {
        this.$toast.error("Fehler beim Laden")
      } finally {
        this.loadingDayDetails = false
      }
    },

    async fetchGFHours(date) {
      this.fetchingGFHours = true
      this.GFHourInfos = { list: [], totalDuration: 0 }
      try {
        let startDate = this.$moment(date).startOf("month").unix()
        let endDate = this.$moment(date).endOf("month").unix()
        this.GFHourInfos = await api.getGFHoursForSelf(startDate, endDate)
      } catch (e) {
        console.error(e)
      } finally {
        this.fetchingGFHours = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* --- VARIABLES --- */
$primary-color: var(--v-primary, #52ae32);
$glass-bg: rgba(255, 255, 255, 0.05);
$glass-border: rgba(255, 255, 255, 0.08);

.my-training-container {
  color: white;
}

.italic-header {
  font-style: italic;
  letter-spacing: -0.5px;
  text-shadow: 0 4px 15px rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
}

/* Glass Card Logic */
.glass-card {
  background: $glass-bg !important;
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border: 1px solid $glass-border;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.5);
}

.glass-inner-card {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.custom-skeleton {
  background: rgba(255, 255, 255, 0.05) !important;
}
::v-deep .v-skeleton-loader__image {
  background: rgba(255, 255, 255, 0.05) !important;
}

/* Kalender Grid Styles */
.day-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
}

.day-name {
  font-size: 0.7rem;
  color: #888;
  margin-bottom: 6px;
  font-weight: 600;
}

.day-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  font-weight: bold;
  color: white;
}

/* Interaktive States */
.day-active .day-circle {
  background: rgba(82, 174, 50, 0.2);
  border-color: $primary-color;
  color: $primary-color;
  box-shadow: 0 0 10px rgba(82, 174, 50, 0.3);
}

.day-today .day-name {
  color: $primary-color;
  font-weight: 900;
}

.day-selected .day-circle {
  background: white !important;
  color: black !important;
  transform: scale(1.15);
  border-color: white !important;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
}

/* Detail Box Styles */
.rest-day-box {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), transparent);
  border: 1px dashed rgba(255, 255, 255, 0.1);
}

.success-gradient-soft {
  background: linear-gradient(90deg, rgba(82, 174, 50, 0.2) 0%, transparent 100%);
}

.border-bottom-light {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.emoji-bounce {
  animation: bounce 2s infinite;
  font-size: 3rem;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(5px);
  }
  60% {
    transform: translateY(3px);
  }
}

/* --- RECHARGE ZONE STYLES --- */

.recharge-card {
  /* Dunkler, tiefer Hintergrund für Kontrast */
  background: linear-gradient(180deg, rgba(20, 20, 20, 0.6) 0%, rgba(0, 0, 0, 0.4) 100%);
  border: 1px solid rgba(82, 174, 50, 0.3); /* Zartes Grün im Rahmen */
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
}

/* Der schwebende grüne Nebel im Hintergrund */
.ambient-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, rgba(82, 174, 50, 0.4) 0%, transparent 70%);
  transform: translate(-50%, -50%);
  filter: blur(40px);
  animation: breathe 4s ease-in-out infinite;
  z-index: 1;
}

/* Icon Wrapper */
.icon-wrapper {
  position: relative;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 2;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

/* Pulsierender Ring um das Icon */
.pulse-ring {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  border: 2px solid #52ae32;
  animation: ripple 2s cubic-bezier(0, 0.2, 0.8, 1) infinite;
}

/* Pillen-Style für den Status */
.status-pill {
  background: rgba(82, 174, 50, 0.1);
  border: 1px solid rgba(82, 174, 50, 0.2);
  backdrop-filter: blur(4px);
}

/* Utilities */
.z-index-2 {
  position: relative;
  z-index: 2;
}

.tracking-wider {
  letter-spacing: 2px !important;
}

/* ANIMATIONEN */

@keyframes breathe {
  0%,
  100% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(1.3);
  }
}

@keyframes ripple {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.blink {
  animation: blinker 1.5s linear infinite;
}

@keyframes blinker {
  50% {
    opacity: 0.3;
  }
}
</style>
